import { useState, useEffect } from "react";

import { Link } from "@remix-run/react";
import {
    Loader2,
    FolderPlus,
    Check,
    Sparkles,
    AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { ScrollArea } from "~/components/ui/scroll-area";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "~/components/ui/select";
import { H3, P } from "~/components/ui/typography";
import {
    useSuggestPartGroups,
    useAcceptGroupSuggestion,
    useDismissPartSuggestion,
} from "~/hooks/mutations/use-suggest-part-groups";

import type { GroupingType, PartGroupSuggestion, PartGroup } from "../types";

// AIDEV-NOTE: Dialog for reviewing and accepting group suggestions for parts from a session
// Requires existing groupings to be created first (via Parts > Grouping page)
// Also requires at least 5 sessions in the system

const MIN_SESSIONS_REQUIRED = 5;

interface PartGroupSuggestionDialogProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    sessionId: string;
    groupingType: GroupingType;
    onComplete?: () => void;
}

interface GroupSelection {
    groupName: string;
    isNewGroup: boolean;
    newGroupDescription?: string;
}

const NEW_GROUP_VALUE = "__NEW_GROUP__";

export function PartGroupSuggestionDialog({
    isOpen,
    onOpenChange,
    sessionId,
    groupingType,
    onComplete,
}: PartGroupSuggestionDialogProps) {
    const suggestMutation = useSuggestPartGroups();
    const acceptMutation = useAcceptGroupSuggestion();
    const dismissMutation = useDismissPartSuggestion();

    const [suggestions, setSuggestions] = useState<PartGroupSuggestion[]>([]);
    const [existingGroups, setExistingGroups] = useState<PartGroup[]>([]);
    const [hasExistingGrouping, setHasExistingGrouping] = useState(true);
    const [sessionCount, setSessionCount] = useState(MIN_SESSIONS_REQUIRED);
    const [selections, setSelections] = useState<Map<string, GroupSelection>>(
        new Map(),
    );
    const [isSaving, setIsSaving] = useState(false);
    const [dismissingPartIds, setDismissingPartIds] = useState<Set<string>>(
        new Set(),
    );

    // Collect all unique group names from suggestions and existing groups
    const allGroupNames = new Set<string>([
        ...existingGroups.map((g) => g.name),
        ...suggestions
            .filter((s) => s.recommendation.type === "existing")
            .map((s) => s.recommendation.groupName),
    ]);

    // New groups suggested by AI
    const suggestedNewGroups = suggestions
        .filter((s) => s.recommendation.type === "new")
        .reduce((acc, s) => {
            if (!acc.has(s.recommendation.groupName)) {
                acc.set(s.recommendation.groupName, {
                    name: s.recommendation.groupName,
                    description: s.recommendation.groupDescription || "",
                });
            }
            return acc;
        }, new Map<string, { name: string; description: string }>());

    // Fetch suggestions when dialog opens
    useEffect(() => {
        if (isOpen && suggestions.length === 0 && !suggestMutation.isPending) {
            suggestMutation.mutate(
                { sessionId, groupingType },
                {
                    onSuccess: (data) => {
                        setSuggestions(data.suggestions);
                        setExistingGroups(data.existingGroups);
                        setHasExistingGrouping(data.hasExistingGrouping);
                        setSessionCount(data.sessionCount);

                        // Initialize selections with AI recommendations
                        const initialSelections = new Map<
                            string,
                            GroupSelection
                        >();
                        for (const suggestion of data.suggestions) {
                            initialSelections.set(suggestion.partId, {
                                groupName: suggestion.recommendation.groupName,
                                isNewGroup:
                                    suggestion.recommendation.type === "new",
                                newGroupDescription:
                                    suggestion.recommendation.groupDescription,
                            });
                        }
                        setSelections(initialSelections);
                    },
                },
            );
        }
    }, [isOpen, sessionId, groupingType]);

    // Reset state when dialog closes
    useEffect(() => {
        if (!isOpen) {
            setSuggestions([]);
            setExistingGroups([]);
            setHasExistingGrouping(true);
            setSessionCount(MIN_SESSIONS_REQUIRED);
            setSelections(new Map());
        }
    }, [isOpen]);

    const handleSelectionChange = (
        partId: string,
        value: string,
        suggestion: PartGroupSuggestion,
    ) => {
        const newSelections = new Map(selections);

        if (value === NEW_GROUP_VALUE) {
            // User wants to create a custom new group
            newSelections.set(partId, {
                groupName: "",
                isNewGroup: true,
                newGroupDescription: "",
            });
        } else {
            // Check if this is a suggested new group
            const suggestedNew = suggestedNewGroups.get(value);
            newSelections.set(partId, {
                groupName: value,
                isNewGroup: !!suggestedNew,
                newGroupDescription: suggestedNew?.description,
            });
        }

        setSelections(newSelections);
    };

    const handleNewGroupNameChange = (partId: string, name: string) => {
        const newSelections = new Map(selections);
        const current = newSelections.get(partId);
        if (current) {
            newSelections.set(partId, {
                ...current,
                groupName: name,
            });
        }
        setSelections(newSelections);
    };

    const handleNewGroupDescriptionChange = (
        partId: string,
        description: string,
    ) => {
        const newSelections = new Map(selections);
        const current = newSelections.get(partId);
        if (current) {
            newSelections.set(partId, {
                ...current,
                newGroupDescription: description,
            });
        }
        setSelections(newSelections);
    };

    const handleSaveAll = async () => {
        setIsSaving(true);

        try {
            const promises = [];
            for (const [partId, selection] of selections) {
                if (!selection.groupName) continue;

                promises.push(
                    acceptMutation.mutateAsync({
                        partId,
                        groupingType,
                        groupName: selection.groupName,
                        isNewGroup: selection.isNewGroup,
                        groupDescription: selection.newGroupDescription,
                    }),
                );
            }

            await Promise.all(promises);

            toast.success(`Assigned ${promises.length} parts to groups`);
            onComplete?.();
            onOpenChange(false);
        } catch (error) {
            console.error("Failed to save group assignments:", error);
            toast.error("Failed to save some group assignments");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDismissPart = async (partId: string) => {
        setDismissingPartIds((prev) => new Set([...prev, partId]));

        try {
            await dismissMutation.mutateAsync({ partId, groupingType });

            // Remove the dismissed part from local state
            setSuggestions((prev) => prev.filter((s) => s.partId !== partId));
            setSelections((prev) => {
                const newSelections = new Map(prev);
                newSelections.delete(partId);
                return newSelections;
            });

            toast.success(
                "Part marked as new (won't appear in future suggestions)",
            );
        } catch (error) {
            console.error("Failed to dismiss part:", error);
        } finally {
            setDismissingPartIds((prev) => {
                const newSet = new Set(prev);
                newSet.delete(partId);
                return newSet;
            });
        }
    };

    const getConfidenceBadgeVariant = (
        confidence: "high" | "medium" | "low",
    ) => {
        switch (confidence) {
            case "high":
                return "default";
            case "medium":
                return "secondary";
            case "low":
                return "outline";
        }
    };

    const isLoading = suggestMutation.isPending;
    const hasSelections = selections.size > 0;
    const allSelectionsValid = Array.from(selections.values()).every(
        (s) => s.groupName,
    );

    return (
        <Dialog
            open={isOpen}
            onOpenChange={onOpenChange}
            analyticsId="part-group-suggestion-dialog"
        >
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="size-5" />
                        Suggest Groups for Parts
                    </DialogTitle>
                    <DialogDescription>
                        {groupingType === "CORE_WOUND"
                            ? "Assign exiles to core wound groups based on the burdens they carry."
                            : "Assign parts to life area groups based on where they show up."}
                    </DialogDescription>
                </DialogHeader>

                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-12">
                        <Loader2 className="size-8 animate-spin text-muted-foreground" />
                        <P className="mt-4 text-muted-foreground">
                            Analyzing parts for group suggestions...
                        </P>
                    </div>
                ) : !hasExistingGrouping ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <AlertCircle className="size-12 text-muted-foreground mb-4" />
                        <H3 className="mb-2">Create Initial Groups First</H3>
                        <P className="text-muted-foreground mb-4 max-w-md">
                            Before suggesting group assignments, you need to
                            create your initial part groups. Visit the Parts
                            page and use the Grouping feature to organize your
                            parts into meaningful categories.
                        </P>
                        <Link to="/parts/grouping">
                            <Button variant="outline">
                                Go to Parts Grouping
                            </Button>
                        </Link>
                    </div>
                ) : sessionCount < MIN_SESSIONS_REQUIRED ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <AlertCircle className="size-12 text-muted-foreground mb-4" />
                        <H3 className="mb-2">More Sessions Needed</H3>
                        <P className="text-muted-foreground max-w-md">
                            Group suggestions work best with more context. You
                            currently have {sessionCount} session
                            {sessionCount === 1 ? "" : "s"}. This feature will
                            be available once you have at least{" "}
                            {MIN_SESSIONS_REQUIRED} sessions.
                        </P>
                    </div>
                ) : suggestions.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12">
                        <P className="text-muted-foreground">
                            No parts found to assign to groups in this session.
                        </P>
                    </div>
                ) : (
                    <ScrollArea className="max-h-[60vh]">
                        <div className="space-y-4 pr-4">
                            {suggestions.map((suggestion) => {
                                const selection = selections.get(
                                    suggestion.partId,
                                );
                                const isCustomNewGroup =
                                    selection?.isNewGroup &&
                                    !suggestedNewGroups.has(
                                        selection.groupName,
                                    ) &&
                                    selection.groupName !== "";

                                return (
                                    <div
                                        key={suggestion.partId}
                                        className="rounded-lg border p-4"
                                    >
                                        <div className="mb-3 flex items-start justify-between">
                                            <div>
                                                <H3 className="mb-1 text-base">
                                                    {suggestion.partName}
                                                </H3>
                                                <P className="text-sm text-muted-foreground">
                                                    {
                                                        suggestion
                                                            .recommendation
                                                            .reasoning
                                                    }
                                                </P>
                                            </div>
                                            <Badge
                                                variant={getConfidenceBadgeVariant(
                                                    suggestion.recommendation
                                                        .confidence,
                                                )}
                                            >
                                                {
                                                    suggestion.recommendation
                                                        .confidence
                                                }
                                            </Badge>
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Assign to group</Label>
                                            <Select
                                                value={
                                                    selection?.groupName === ""
                                                        ? NEW_GROUP_VALUE
                                                        : selection?.groupName ||
                                                          ""
                                                }
                                                onValueChange={(value) =>
                                                    handleSelectionChange(
                                                        suggestion.partId,
                                                        value,
                                                        suggestion,
                                                    )
                                                }
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select a group" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {/* Existing groups */}
                                                    {Array.from(
                                                        allGroupNames,
                                                    ).map((groupName) => (
                                                        <SelectItem
                                                            key={groupName}
                                                            value={groupName}
                                                        >
                                                            {groupName}
                                                        </SelectItem>
                                                    ))}

                                                    {/* AI-suggested new groups */}
                                                    {Array.from(
                                                        suggestedNewGroups.entries(),
                                                    ).map(([name, group]) => (
                                                        <SelectItem
                                                            key={`new-${name}`}
                                                            value={name}
                                                        >
                                                            <span className="flex items-center gap-2">
                                                                <FolderPlus className="size-4" />
                                                                {name} (new)
                                                            </span>
                                                        </SelectItem>
                                                    ))}

                                                    {/* Custom new group option */}
                                                    <SelectItem
                                                        value={NEW_GROUP_VALUE}
                                                    >
                                                        <span className="flex items-center gap-2">
                                                            <FolderPlus className="size-4" />
                                                            Create new group...
                                                        </span>
                                                    </SelectItem>
                                                </SelectContent>
                                            </Select>

                                            {/* Show input fields for custom new group */}
                                            {selection?.groupName === "" &&
                                                selection.isNewGroup && (
                                                    <div className="mt-2 space-y-2">
                                                        <Input
                                                            placeholder="New group name"
                                                            value={
                                                                selection.groupName
                                                            }
                                                            onChange={(e) =>
                                                                handleNewGroupNameChange(
                                                                    suggestion.partId,
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                        />
                                                        <Input
                                                            placeholder="Group description"
                                                            value={
                                                                selection.newGroupDescription ||
                                                                ""
                                                            }
                                                            onChange={(e) =>
                                                                handleNewGroupDescriptionChange(
                                                                    suggestion.partId,
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                )}

                                            {/* This is a new part button - dismisses suggestions */}
                                            <div className="flex justify-end pt-2">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() =>
                                                        handleDismissPart(
                                                            suggestion.partId,
                                                        )
                                                    }
                                                    disabled={
                                                        dismissingPartIds.has(
                                                            suggestion.partId,
                                                        ) || isSaving
                                                    }
                                                >
                                                    {dismissingPartIds.has(
                                                        suggestion.partId,
                                                    ) ? (
                                                        <>
                                                            <Loader2 className="mr-2 size-4 animate-spin" />
                                                            Dismissing...
                                                        </>
                                                    ) : (
                                                        "This is a new part"
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </ScrollArea>
                )}

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveAll}
                        disabled={
                            isLoading ||
                            !hasSelections ||
                            !allSelectionsValid ||
                            isSaving
                        }
                    >
                        {isSaving ? (
                            <>
                                <Loader2 className="mr-2 size-4 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <Check className="mr-2 size-4" />
                                Save All
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
